import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    Typography,
    InputLabel,
    Tooltip,
    FormHelperText,
    TextField,
    IconButton,
    makeStyles
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import SignaturePad from 'react-signature-canvas';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import moment from 'moment';
import AppContext from '../../../../App/AppContext';
import { AppStorage } from '../../../../Core/Service/storage-service';
import { getSingleChecks, postSingleChecksData } from '../../../../Core/Service/vehicleTabChecks';
import { AppButtonGroup, SecondaryButton } from '../../../../Core/FormInput';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: '16px 20px 20px',
        boxSizing: 'border-box',
        overflowX: 'hidden'
    },
    defectCard: {
        padding: '0px 0px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        boxSizing: 'border-box'
    },
    defectIcon: {
        color: '#e53e3e',
        fontSize: 20,
        flexShrink: 0
    },
    defectContent: {
        flex: 1
    },
    defectLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: '#e53e3e',
        display: 'inline-block',
        marginRight: 6
    },
    defectText: {
        fontSize: 14,
        fontWeight: 600,
        color: '#e53e3e',
        wordBreak: 'break-word',
        display: 'inline'
    },
    fieldLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: '#374151',
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center'
    },
    requiredStar: {
        color: '#e53e3e',
        marginLeft: 3
    },
    statusGroupWrapper: {
        width: '100%',
        '& .app-btn-group': {
            width: '100%',
            display: 'flex'
        },
        '& .app-select-btn-grp': {
            width: '100%',
            display: 'flex'
        },
        '& .MuiToggleButtonGroup-root': {
            width: '100%',
            display: 'flex'
        },
        '& .MuiToggleButton-root': {
            flex: 1,
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: 500,
            textTransform: 'none',
            border: '1px solid #d1d5db',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
                backgroundColor: '#38a169 !important',
                color: '#ffffff !important',
                fontWeight: 600
            },
            '&:hover': {
                backgroundColor: '#f3f4f6'
            },
            '&.Mui-selected:hover': {
                backgroundColor: '#2f855a !important'
            }
        }
    },
    sigHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: 6
    },
    sigActionBtns: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    actionIconBtn: {
        padding: 5,
        color: '#4b5563',
        '&:hover': {
            backgroundColor: '#f3f4f6',
            color: '#111827'
        }
    },
    deleteIconBtn: {
        padding: 5,
        color: '#ef4444',
        '&:hover': {
            backgroundColor: '#fee2e2',
            color: '#b91c1c'
        }
    },
    sigBox: {
        border: '1px solid #d1d5db',
        borderRadius: 8,
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            borderColor: '#9ca3af'
        }
    },
    sigBoxError: {
        borderColor: '#e53e3e !important'
    },
    sigPadContainer: {
        width: '100%',
        height: 150,
        backgroundColor: '#ffffff',
        position: 'relative',
        '& .sigPad': {
            width: '100%',
            height: '100%',
            cursor: 'crosshair',
            display: 'block'
        }
    },
    sigImagePreview: {
        position: 'relative',
        width: '100%',
        height: 150,
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 6,
        boxSizing: 'border-box'
    },
    sigImage: {
        maxWidth: '100%',
        maxHeight: '138px',
        objectFit: 'contain',
        display: 'block'
    },
    sigMetaBadge: {
        position: 'absolute',
        right: 10,
        bottom: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        padding: '3px 8px',
        borderRadius: 4,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid #e5e7eb'
    },
    sigMetaName: {
        fontSize: 12,
        fontWeight: 600,
        color: '#1f2937'
    },
    sigMetaDate: {
        fontSize: 10,
        color: '#6b7280'
    },
    submitBtnGrid: {
        marginTop: 10,
        width: '100%'
    },
    fullWidthSubmitBtn: {
        width: '100% !important',
        height: 44,
        fontSize: '15px !important',
        fontWeight: '600 !important',
        letterSpacing: '0.5px',
        borderRadius: '6px !important',
        textTransform: 'uppercase'
    },
    errorText: {
        color: '#e53e3e !important',
        fontSize: '12px !important',
        marginTop: '4px !important',
        marginLeft: '2px !important'
    }
}));

export const ActionTakenModal = (props) => {
    const classes = useStyles();
    const { showToast } = useContext(AppContext);
    const [btnLoader, setBtnLoader] = useState(false);

    const inputRef = useRef(null);
    const inputHiddenRef = useRef(null); // Hidden input to shift focus

    const [state, setState] = useState({
        errors: {},
        statusList: [],
        signAvailable: AppStorage.getSignature() === 'absent' ? false : true
    });
    const signCanvas = useRef({});

    useEffect(() => {
        getCheckData();
    }, []);

    const getCheckData = async () => {
        let res = await getSingleChecks(props.selectedCheck.inspectionSheetCheckResultID);
        if (res.success) {
            let data = res.data?.defect?.[0];

            setState((st) => {
                let newSt = { ...st };
                if (data) {
                    newSt.defectDetail = data.defect;
                    newSt.vehicleInspectionsDefectActionTaken = data.vehicleInspectionsDefectActionTaken;
                    newSt.vehicleInspectionsDefectRectifiedSignature = data.vehicleInspectionsDefectRectifiedSignature;
                    newSt.vehicleInspectionsDefectStatusID = data.vehicleInspectionsDefectStatusID;
                    newSt.imgtechniciansign = data.vehicleInspectionsDefectRectifiedSignature ? true : false;
                    newSt.isDisabled = data.rectifiedBy ? true : false;
                    newSt.vehicleInspectionsDefectRectifiedDate = data.vehicleInspectionsDefectRectifiedDate
                        ? moment(data.vehicleInspectionsDefectRectifiedDate).format('DD-MM-YYYY HH:mm')
                        : '';
                    newSt.vehicleInspectionsDefectRectifiedName = data.vehicleInspectionsDefectRectifiedName;
                    newSt.vehicleInspectionsDefectID = data.vehicleInspectionsDefectID;
                }
                newSt.statusList = res.data?.list || [];
                return newSt;
            });
        } else {
            showToast(res.message);
        }
    };

    const inputChange = useCallback((e) => {
        const { name, value } = e.target;
        setState((st) => ({
            ...st,
            [name]: value,
            errors: { ...st.errors, [name]: undefined }
        }));
    }, []);

    const handleBlur = () => {
        if (inputRef.current) {
            setTimeout(() => {
                inputRef.current.blur();
                if (inputHiddenRef.current) {
                    inputHiddenRef.current.focus();
                }
            }, 0);
        }
    };

    const validations = () => {
        let formIsValid = true;
        let errors = {};

        if (state.vehicleInspectionsDefectStatusID !== '3' && !state.vehicleInspectionsDefectActionTaken?.trim()) {
            errors.vehicleInspectionsDefectActionTaken = `Action taken is required`;
            formIsValid = false;
        }

        if (state.vehicleInspectionsDefectStatusID == 1 || !state.vehicleInspectionsDefectStatusID) {
            errors.vehicleInspectionsDefectStatusID = `Please select defect status`;
            formIsValid = false;
        }
        if (!state.vehicleInspectionsDefectRectifiedSignature) {
            errors.vehicleInspectionsDefectRectifiedSignature = `Engineer signature is required`;
            formIsValid = false;
        }

        setState((st) => ({
            ...st,
            errors: errors
        }));
        return formIsValid;
    };

    const submitData = async () => {
        if (validations()) {
            let data = {
                vehicleInspectionsDefectID: state.vehicleInspectionsDefectID,
                vehicleInspectionsDefectActionTaken: state.vehicleInspectionsDefectActionTaken,
                vehicleInspectionsDefectRectifiedSignature: state.vehicleInspectionsDefectRectifiedSignature,
                vehicleInspectionsDefectStatusID: state.vehicleInspectionsDefectStatusID
            };
            setBtnLoader(true);
            let res = await postSingleChecksData(data);
            if (res.success) {
                showToast('Defect Details Added Successfully');
                props.onClose(true);
            } else {
                showToast(res.message);
            }
            setBtnLoader(false);
        }
    };

    const useDefaultSig = () => {
        setState((st) => ({
            ...st,
            imgtechniciansign: true,
            vehicleInspectionsDefectRectifiedSignature: AppStorage.getSignature(),
            errors: { ...st.errors, vehicleInspectionsDefectRectifiedSignature: undefined }
        }));
    };

    const clear = () => {
        if (state.imgtechniciansign) {
            setState((st) => ({
                ...st,
                imgtechniciansign: false,
                vehicleInspectionsDefectRectifiedSignature: null
            }));
        } else {
            if (signCanvas.current && typeof signCanvas.current.clear === 'function') {
                signCanvas.current.clear();
            }
            setState((st) => ({
                ...st,
                vehicleInspectionsDefectRectifiedSignature: null
            }));
        }
    };

    const GetTechnicianSign = () => {
        if (signCanvas.current && typeof signCanvas.current.toDataURL === 'function') {
            setState((st) => ({
                ...st,
                vehicleInspectionsDefectRectifiedSignature: signCanvas.current.toDataURL(),
                errors: { ...st.errors, vehicleInspectionsDefectRectifiedSignature: undefined }
            }));
        }
    };

    const onBtnSelected = (o) => {
        setState((dt) => ({
            ...dt,
            ...o,
            errors: { ...dt.errors, vehicleInspectionsDefectStatusID: undefined }
        }));
    };

    return (
        <div className={classes.container}>
            <Grid container spacing={2}>
                {/* Defect Description Banner */}
                {state.defectDetail && (
                    <Grid item xs={12}>
                        <div className={classes.defectCard}>
                            <ReportProblemOutlinedIcon className={classes.defectIcon} />
                            <div className={classes.defectContent}>
                                <div className={classes.defectLabel}>Reported Defect</div>
                                <div className={classes.defectText}>{state.defectDetail}</div>
                            </div>
                        </div>
                    </Grid>
                )}

                {/* Action Taken Input */}
                <Grid item xs={12}>
                    <InputLabel shrink className={classes.fieldLabel}>
                        Action Taken
                    </InputLabel>
                    <TextField
                        size="small"
                        variant="outlined"
                        disabled={state.isDisabled}
                        autoComplete="off"
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Describe the action taken or repairs performed..."
                        value={state.vehicleInspectionsDefectActionTaken || ''}
                        onChange={inputChange}
                        name="vehicleInspectionsDefectActionTaken"
                        error={Boolean(state.errors.vehicleInspectionsDefectActionTaken)}
                        inputRef={inputRef}
                    />
                    {state.errors.vehicleInspectionsDefectActionTaken && (
                        <FormHelperText error className={classes.errorText}>
                            {state.errors.vehicleInspectionsDefectActionTaken}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Defect Status Selection */}
                <Grid item xs={12}>
                    <InputLabel shrink className={classes.fieldLabel}>
                        Defect Status <span className={classes.requiredStar}>*</span>
                    </InputLabel>
                    <div className={classes.statusGroupWrapper}>
                        <AppButtonGroup
                            name="vehicleInspectionsDefectStatusID"
                            onChange={onBtnSelected}
                            value={`${state.vehicleInspectionsDefectStatusID || ''}`}
                            options={state.statusList}
                            valuePropertyName="statusID"
                            textPropertyName="status"
                            disabled={state.isDisabled}
                        />
                    </div>
                    {state.errors.vehicleInspectionsDefectStatusID && (
                        <FormHelperText error className={classes.errorText}>
                            {state.errors.vehicleInspectionsDefectStatusID}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Hidden input to shift focus */}
                <div ref={inputHiddenRef} tabIndex={-1} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />

                {/* Engineer Signature Pad */}
                <Grid item xs={12}>
                    <div className={classes.sigHeader}>
                        <InputLabel shrink className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                            Engineer Signature <span className={classes.requiredStar}>*</span>
                        </InputLabel>
                        <div className={classes.sigActionBtns}>
                            {!state.isDisabled && state.signAvailable && (
                                <Tooltip title="Use saved signature">
                                    <IconButton size="small" className={classes.actionIconBtn} onClick={useDefaultSig}>
                                        <BorderColorIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {!state.isDisabled && (
                                <Tooltip title="Clear signature">
                                    <IconButton size="small" className={classes.deleteIconBtn} onClick={clear}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    <div
                        className={`${classes.sigBox} ${
                            state.errors.vehicleInspectionsDefectRectifiedSignature ? classes.sigBoxError : ''
                        }`}
                    >
                        {state.imgtechniciansign ? (
                            <div className={classes.sigImagePreview}>
                                <img
                                    src={state.vehicleInspectionsDefectRectifiedSignature}
                                    alt="Technician Signature"
                                    className={classes.sigImage}
                                />
                                {(state.vehicleInspectionsDefectRectifiedName || state.vehicleInspectionsDefectRectifiedDate) && (
                                    <div className={classes.sigMetaBadge}>
                                        {state.vehicleInspectionsDefectRectifiedName && (
                                            <Typography className={classes.sigMetaName}>
                                                {state.vehicleInspectionsDefectRectifiedName}
                                            </Typography>
                                        )}
                                        {state.vehicleInspectionsDefectRectifiedDate && (
                                            <Typography className={classes.sigMetaDate}>
                                                {state.vehicleInspectionsDefectRectifiedDate}
                                            </Typography>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={classes.sigPadContainer}>
                                <SignaturePad
                                    canvasProps={{ className: 'sigPad' }}
                                    ref={signCanvas}
                                    onEnd={() => {
                                        GetTechnicianSign();
                                        handleBlur();
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {state.errors.vehicleInspectionsDefectRectifiedSignature && (
                        <FormHelperText error className={classes.errorText}>
                            {state.errors.vehicleInspectionsDefectRectifiedSignature}
                        </FormHelperText>
                    )}
                </Grid>

                {/* Submit / Confirm Button Filling the Entire Row */}
                <Grid item xs={12} className={classes.submitBtnGrid}>
                    {!state.isDisabled ? (
                        <SecondaryButton
                            fullWidth
                            className={classes.fullWidthSubmitBtn}
                            onClick={submitData}
                            isBtnLoader={btnLoader}
                        >
                            Confirm
                        </SecondaryButton>
                    ) : (
                        <SecondaryButton
                            fullWidth
                            className={classes.fullWidthSubmitBtn}
                            onClick={() => props.onClose(false)}
                        >
                            Close
                        </SecondaryButton>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

export default ActionTakenModal;
